import { useState, type FormEvent } from "react";
import { api } from "../api/client";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/Icon";
import { LoadingRows, ResourceError } from "../components/ResourceNotice";
import { useAuth } from "../context/AuthContext";
import { useRemoteResource } from "../hooks/useRemoteResource";
import { formatDate, statusLabel } from "../utils/format";

export function CreditsPage() {
  const { user, refresh: refreshUser } = useAuth();
  const balance = useRemoteResource(api.credits.balance);
  const ledger = useRemoteResource(api.credits.ledger);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  async function redeem(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (code.trim().length < 6) return setMessage({ tone: "error", text: "请输入完整卡密" });
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.credits.redeem(code.trim());
      setCode("");
      setMessage({ tone: "success", text: `兑换成功，积分增加 ${result.points}` });
      await refreshUser();
      balance.reload();
      ledger.reload();
    } catch (reason) {
      setMessage({ tone: "error", text: reason instanceof Error ? reason.message : "兑换失败" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack credits-page">
      <section className="credit-vault">
        <div className="credit-vault__balance"><span>AVAILABLE CREDITS</span><b>{balance.data?.balance ?? user?.credit_balance ?? 0}</b><small>每个成功授权账号扣除 1 分</small></div>
        <div className="credit-vault__art" aria-hidden="true"><span>OA</span><i /><i /></div>
        <form className="redeem-form" onSubmit={redeem}>
          <label htmlFor="redeem-code">兑换卡密</label>
          <div><input id="redeem-code" value={code} onChange={(event) => setCode(event.target.value)} placeholder="OA-XXXX-XXXX-XXXX" autoComplete="off" disabled={busy || balance.unsupported} /><button className="button button--amber" disabled={busy || balance.unsupported}>{busy ? <span className="button-spinner" /> : null}{busy ? "兑换中" : "确认兑换"}</button></div>
          <p className={message ? `form-feedback form-feedback--${message.tone}` : "form-feedback"} aria-live="polite">{message?.text || "卡密只会被使用一次，兑换记录会写入积分流水。"}</p>
        </form>
      </section>

      {balance.error ? <ResourceError message={balance.error} onRetry={balance.reload} /> : null}
      {balance.unsupported ? <EmptyState icon="credits" title="当前环境未开放积分接口" description="旁路页面不会模拟余额或兑换成功。" /> : null}

      <section className="ledger-zone">
        <div className="section-heading"><div><span className="eyebrow">ACCOUNT LEDGER</span><h2>积分流水</h2></div><button className="icon-button" onClick={ledger.reload} aria-label="刷新积分流水"><Icon name="refresh" size={19} /></button></div>
        {ledger.loading ? <LoadingRows count={5} /> : null}
        {ledger.error ? <ResourceError message={ledger.error} onRetry={ledger.reload} /> : null}
        {!ledger.loading && !ledger.error && !ledger.unsupported && !ledger.data?.items.length ? <EmptyState compact icon="credits" title="还没有积分变动" description="兑换或任务成功后，流水会出现在这里。" /> : null}
        <div className="ledger-list">
          {ledger.data?.items.map((item) => (
            <article key={item.id}>
              <span className={`ledger-list__amount${item.amount >= 0 ? " is-positive" : ""}`}>{item.amount > 0 ? "+" : ""}{item.amount}</span>
              <div><b>{statusLabel(item.type)}</b><p>{item.remark || "积分变动"}</p></div>
              <span><small>变动后余额</small><b>{item.balance_after}</b></span>
              <time>{formatDate(item.created_at)}</time>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
